import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mic, GraduationCap, Building2, Gamepad2, Users, BarChart3, ArrowRight, Check } from 'lucide-react';
import { useT } from '@/lib/i18n';

/**
 * UseCases — 6 selectable personas. Selecting one updates:
 *   - example content type
 *   - recommended workflow (3 steps)
 *   - generated clip preview list
 *   - benefits bullet list
 */

const ACC = '#D4FF00';

const PERSONAS = {
  podcaster: {
    icon: Mic,
    labelFr: 'Podcasteur',   labelEn: 'Podcaster',
    example: 'Podcast · 60-90 min',
    workflow: [
      'Upload de ton épisode complet',
      'IA détecte les 8-12 meilleurs moments',
      'Publie 1 clip par jour pendant 2 semaines',
    ],
    workflowEn: [
      'Upload your full episode',
      'AI detects the top 8-12 moments',
      'Publish 1 clip per day for 2 weeks',
    ],
    clips: ['The rule nobody talks about', '"Wait — did he just say that?"', 'The moment everything shifted'],
    benefitsFr: ['1 podcast = 2 semaines de contenu', 'Sous-titres CapCut auto', 'Coach IA sur chaque clip'],
    benefitsEn: ['1 podcast = 2 weeks of content', 'CapCut-style captions auto', 'AI Coach on every clip'],
  },
  coach: {
    icon: GraduationCap,
    labelFr: 'Coach',        labelEn: 'Coach',
    example: 'Live · Q&A · 45 min',
    workflow: [
      'Upload de ton live coaching',
      'IA repère les insights punchy',
      'Publie sur LinkedIn + Instagram',
    ],
    workflowEn: [
      'Upload your live session',
      'AI spots the punchy insights',
      'Publish to LinkedIn + Instagram',
    ],
    clips: ['The mindset shift', 'Why 90% fail here', 'One question changes everything'],
    benefitsFr: ['Autorité sur LinkedIn', 'Clips de vente sans montage', 'Analytics par plateforme'],
    benefitsEn: ['LinkedIn authority', 'Sales clips zero editing', 'Per-platform analytics'],
  },
  agency: {
    icon: Building2,
    labelFr: 'Agence',       labelEn: 'Agency',
    example: 'Multi-client · Batch',
    workflow: [
      'Upload en batch pour tes clients',
      'IA génère + AI Coach personnalisé',
      'Livre les clips prêts à publier',
    ],
    workflowEn: [
      'Batch upload for your clients',
      'AI + personalised AI Coach',
      'Deliver ready-to-post clips',
    ],
    clips: ['Client A · 96 viral', 'Client B · 92 viral', 'Client C · 89 viral'],
    benefitsFr: ['5× plus de livrables', 'Growth Dashboard par compte', 'Marge nette × 3'],
    benefitsEn: ['5× more deliverables', 'Growth Dashboard per account', '3× net margin'],
  },
  business: {
    icon: BarChart3,
    labelFr: 'Business',     labelEn: 'Business',
    example: 'All-hands · Earnings · Webinar',
    workflow: [
      'Upload de ton webinar ou all-hands',
      'IA extrait les moments-clés',
      'Publie sur LinkedIn / YouTube',
    ],
    workflowEn: [
      'Upload your webinar or all-hands',
      'AI extracts key moments',
      'Publish on LinkedIn / YouTube',
    ],
    clips: ['Our biggest hiring mistake', 'The metric nobody tracks', '"Don\'t optimize for that"'],
    benefitsFr: ['Content marketing automatisé', 'Employee advocacy scalable', 'Reach organique boosté'],
    benefitsEn: ['Automated content marketing', 'Scalable employee advocacy', 'Boosted organic reach'],
  },
  gaming: {
    icon: Gamepad2,
    labelFr: 'Gaming',       labelEn: 'Gaming',
    example: 'Stream · 2-4 h',
    workflow: [
      'Upload de ton VOD Twitch',
      'IA repère les plays et les moments drôles',
      'Publie sur TikTok · Shorts',
    ],
    workflowEn: [
      'Upload your Twitch VOD',
      'AI spots plays + funny moments',
      'Publish to TikTok · Shorts',
    ],
    clips: ['1v5 clutch — no way', 'The play of the year', 'How did that even happen'],
    benefitsFr: ['Highlights sans monter', 'Cross-post TikTok + Shorts', 'Growth Dashboard viewership'],
    benefitsEn: ['Highlights, no editing', 'Cross-post TikTok + Shorts', 'Growth Dashboard viewership'],
  },
  education: {
    icon: Users,
    labelFr: 'Éducation',    labelEn: 'Education',
    example: 'Cours · Talk · Panel',
    workflow: [
      'Upload de ton cours ou talk',
      'IA identifie les concepts fondamentaux',
      'Diffuse en micro-content',
    ],
    workflowEn: [
      'Upload your class or talk',
      'AI identifies core concepts',
      'Distribute as micro-content',
    ],
    clips: ['The 3 laws of...', 'Why this always breaks', 'The counter-intuitive truth'],
    benefitsFr: ['Micro-learning scalable', 'Sous-titres multi-langues', 'Engagement × 4 sur les shorts'],
    benefitsEn: ['Scalable micro-learning', 'Multi-language captions', '4× engagement on shorts'],
  },
};

const UseCases = () => {
  const { lang } = useT();
  const [active, setActive] = useState('podcaster');
  const p = PERSONAS[active];
  const workflow = lang === 'fr' ? p.workflow : p.workflowEn;
  const benefits = lang === 'fr' ? p.benefitsFr : p.benefitsEn;

  return (
    <section id="use-cases" className="py-24 px-6 lg:px-8" data-testid="landing-use-cases">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5 font-mono">
            {lang === 'fr' ? 'Cas d\'usage' : 'Use cases'}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
            {lang === 'fr' ? 'Fait pour ton contenu' : 'Built for your content'}
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm sm:text-base">
            {lang === 'fr' ? 'Sélectionne ton profil — vois exactement comment HighlightAI travaille pour toi.' : 'Pick your profile — see exactly how HighlightAI works for you.'}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="use-cases-tabs">
          {Object.entries(PERSONAS).map(([key, per]) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`use-case-tab-${key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold transition-colors ${
                active === key
                  ? 'text-black'
                  : 'text-zinc-400 border-white/10 bg-white/[0.02] hover:text-zinc-200 hover:border-white/25'
              }`}
              style={active === key ? { backgroundColor: ACC, borderColor: ACC } : {}}
            >
              <per.icon className="w-4 h-4" />
              {lang === 'fr' ? per.labelFr : per.labelEn}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.28 }}
            className="grid lg:grid-cols-3 gap-4"
            data-testid={`use-case-content-${active}`}
          >
            <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 lg:col-span-1">
              <div className="w-12 h-12 rounded-xl bg-[#D4FF00]/10 border border-[#D4FF00]/25 flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5 text-[#D4FF00]" />
              </div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-1">{lang === 'fr' ? 'Exemple' : 'Example'}</div>
              <div className="font-heading text-lg font-bold mb-6">{p.example}</div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">{lang === 'fr' ? 'Workflow' : 'Workflow'}</div>
              <ol className="space-y-2.5">
                {workflow.map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-200">
                    <span className="w-5 h-5 shrink-0 rounded-full bg-[#D4FF00] text-black text-[10px] font-bold flex items-center justify-center tabular-nums mt-0.5">{i + 1}</span>
                    <span className="leading-snug">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 lg:col-span-1">
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-4">{lang === 'fr' ? 'Clips générés' : 'Generated clips'}</div>
              <div className="grid grid-cols-3 gap-2">
                {p.clips.map((c, i) => (
                  <div key={i} className="relative aspect-[9/16] rounded-lg overflow-hidden border border-white/10" style={{ background: `linear-gradient(135deg, hsl(${i * 80 + 40}, 45%, 22%), #0a0a0a)` }}>
                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${ACC}25`, color: ACC, border: `1px solid ${ACC}66` }}>
                      {90 + i * 2}
                    </div>
                    <div className="absolute inset-x-1 bottom-2">
                      <div className="tiktok-caption text-[9px] leading-tight text-center">{c.slice(0, 18).toUpperCase()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[#D4FF00]/25 bg-[#D4FF00]/[0.04] p-6 lg:col-span-1 flex flex-col">
              <div className="text-[10px] uppercase tracking-widest font-mono text-[#D4FF00] mb-4">{lang === 'fr' ? 'Bénéfices' : 'Benefits'}</div>
              <ul className="space-y-3 flex-1">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-100">
                    <Check className="w-4 h-4 text-[#D4FF00] mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/register" data-testid={`use-case-cta-${active}`} className="mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#D4FF00] text-black font-semibold text-sm hover:bg-[#B3D600] transition-colors">
                {lang === 'fr' ? 'Démarrer gratuitement' : 'Start free'} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default UseCases;
